import { useState, useEffect } from "react";
import axios from "axios";
import { AppNavigation } from "../components/AppNavigation";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Check,
  CreditCard,
  Sparkles,
  Loader2,
  Crown,
  Users,
  Wand2,
  Eye,
  FileText,
  PenLine,
  BarChart3,
  HeadphonesIcon,
  Clock,
  Gift,
  Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SubscriptionPage = ({ user, logout, token }) => {
  const [plans, setPlans] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/subscription/plans`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPlans(plansRes.data.plans || []);
      setSubscriptionStatus(statusRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar los planes");
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/subscription/start-trial`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(response.data.message || "¡Período de prueba activado!");
      fetchData();
    } catch (error) {
      console.error("Error starting trial:", error);
      const errorMessage = error.response?.data?.detail || "Error al iniciar el período de prueba";
      toast.error(errorMessage);
    } finally {
      setStartingTrial(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessingPayment(true);
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${BACKEND_URL}/api/subscription/checkout`,
        {
          plan_id: planId,
          origin_url: originUrl
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      const errorMessage = error.response?.data?.detail || "Error al procesar el pago";
      toast.error(errorMessage);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("¿Estás seguro de que deseas cancelar tu suscripción?")) {
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/subscription/cancel`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success("Suscripción cancelada");
      fetchData();
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast.error("Error al cancelar la suscripción");
    }
  };

  const featureIcons = {
    "Gestión de hasta 3 cuentas": <Users className="w-4 h-4 text-primary" />,
    "Asistente IA ilimitado": <Wand2 className="w-4 h-4 text-primary" />,
    "Asesor de competencia": <Eye className="w-4 h-4 text-primary" />,
    "Plantillas de contenido": <FileText className="w-4 h-4 text-primary" />,
    "Firmas y contactos": <PenLine className="w-4 h-4 text-primary" />,
    "Analytics avanzados": <BarChart3 className="w-4 h-4 text-primary" />,
    "Soporte prioritario": <HeadphonesIcon className="w-4 h-4 text-primary" />
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppNavigation user={user} logout={logout} />
        <main className="lg:pl-64">
          <div className="p-6 flex items-center justify-center min-h-screen">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </main>
      </div>
    );
  }

  const isOnTrial = subscriptionStatus?.status === "trial";
  const hasActiveSubscription = subscriptionStatus?.has_subscription && !isOnTrial;
  const canStartTrial = subscriptionStatus?.trial_available !== false && !subscriptionStatus?.has_subscription;

  return (
    <div className="min-h-screen bg-background" data-testid="subscription-page">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:pl-64">
        <div className="p-6 max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 text-primary px-4 py-2 rounded-full mb-4 glow-cyan">
              <Crown className="w-4 h-4" />
              <span className="text-sm font-medium">Planes de Suscripción</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">
              Potencia tu presencia en <span className="gradient-neural-text">redes sociales</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Accede a todas las funcionalidades de StratIA y lleva tu contenido al siguiente nivel
            </p>
          </div>

          {/* Trial Banner - Only show if trial is available */}
          {canStartTrial && (
            <Card className="mb-8 border-accent/50 bg-gradient-to-r from-accent/10 to-primary/5 glow-neural">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 gradient-neural rounded-full flex items-center justify-center glow-purple">
                      <Gift className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl flex items-center gap-2">
                        ¡Prueba Gratis 7 Días!
                        <Badge className="gradient-neural text-white">Nuevo</Badge>
                      </h3>
                      <p className="text-muted-foreground">
                        Accede a todas las funciones Pro sin compromiso. Sin tarjeta requerida.
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="lg"
                    className="gradient-neural glow-neural text-white"
                    onClick={handleStartTrial}
                    disabled={startingTrial}
                    data-testid="start-trial-btn"
                  >
                    {startingTrial ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Activando...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Empezar Prueba Gratis
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Current Trial Status */}
          {isOnTrial && (
            <Card className="mb-8 border-accent/50 bg-accent/5 glow-purple">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 gradient-neural rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        {subscriptionStatus.plan_name}
                        <Badge className="gradient-neural text-white">
                          Prueba Gratuita
                        </Badge>
                      </h3>
                      <p className="text-muted-foreground">
                        Te quedan <strong className="text-primary">{subscriptionStatus.trial_days_remaining} días</strong> de prueba gratuita
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-2xl font-bold gradient-neural-text">
                      GRATIS
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Después €{subscriptionStatus.next_price?.toFixed(2)}/mes
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-3">
                    ¿Te gusta StratIA? Suscríbete ahora y obtén el precio promocional de €4.99/mes durante los primeros 2 meses.
                  </p>
                  <Button 
                    onClick={() => handleSubscribe("stratia_pro")}
                    disabled={processingPayment}
                    className="gradient-neural glow-neural text-white"
                    data-testid="upgrade-from-trial-btn"
                  >
                    {processingPayment ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Suscribirme Ahora
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Current Active Subscription Status */}
          {hasActiveSubscription && (
            <Card className="mb-8 border-primary/30 bg-primary/5 glow-cyan">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 gradient-neural rounded-full flex items-center justify-center">
                      <Crown className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">
                        {subscriptionStatus.plan_name}
                        <Badge variant="secondary" className="ml-2 bg-primary/20 text-primary">
                          {subscriptionStatus.status === "promo" ? "Promoción" : "Activo"}
                        </Badge>
                      </h3>
                      <p className="text-muted-foreground">
                        {subscriptionStatus.status === "promo" 
                          ? `${subscriptionStatus.promo_months_remaining} meses restantes a precio promocional`
                          : "Suscripción activa"
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-2xl font-bold gradient-neural-text">
                      €{subscriptionStatus.current_price?.toFixed(2)}
                      <span className="text-sm text-muted-foreground font-normal">/mes</span>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleCancelSubscription}
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                      data-testid="cancel-subscription-btn"
                    >
                      Cancelar Suscripción
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pricing Cards */}
          <div className="grid gap-8 md:grid-cols-2">
            {/* Free Plan */}
            <Card className="relative">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl">Plan Gratuito</CardTitle>
                <CardDescription>Para comenzar a explorar</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold">€0</span>
                  <span className="text-muted-foreground">/mes</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-muted-foreground" />
                  <span className="text-muted-foreground">1 cuenta de red social</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-muted-foreground" />
                  <span className="text-muted-foreground">5 posts programados/mes</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-muted-foreground" />
                  <span className="text-muted-foreground">Análisis básico</span>
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  variant="outline" 
                  className="w-full" 
                  disabled
                  data-testid="free-plan-btn"
                >
                  {subscriptionStatus?.has_subscription ? "Plan Básico" : "Plan Actual"}
                </Button>
              </CardFooter>
            </Card>

            {/* Pro Plan */}
            {plans.map((plan) => (
              <Card 
                key={plan.id} 
                className="relative border-primary/50 shadow-lg glow-neural"
              >
                {/* Popular Badge */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="gradient-neural text-white px-4 py-1">
                    <Sparkles className="w-3 h-3 mr-1" />
                    {canStartTrial ? "7 Días Gratis" : "Más Popular"}
                  </Badge>
                </div>

                <CardHeader className="pb-4 pt-8">
                  <CardTitle className="text-xl flex items-center gap-2">
                    {plan.name}
                    <Crown className="w-5 h-5 text-accent" />
                  </CardTitle>
                  <CardDescription>Todo lo que necesitas para crecer</CardDescription>
                  <div className="mt-4 space-y-1">
                    {canStartTrial ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold gradient-neural-text">GRATIS</span>
                        </div>
                        <p className="text-sm text-primary font-medium">
                          Los primeros 7 días
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Después €{plan.promo_price?.toFixed(2)}/mes por 2 meses, luego €{plan.regular_price?.toFixed(2)}/mes
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold gradient-neural-text">€{plan.promo_price?.toFixed(2)}</span>
                          <span className="text-muted-foreground">/mes</span>
                        </div>
                        <p className="text-sm text-primary font-medium">
                          Los primeros {plan.promo_months} meses
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Después €{plan.regular_price?.toFixed(2)}/mes
                        </p>
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {plan.features?.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      {featureIcons[feature] || <Check className="w-4 h-4 text-primary" />}
                      <span>{feature}</span>
                    </div>
                  ))}
                </CardContent>
                <CardFooter>
                  {hasActiveSubscription ? (
                    <Button 
                      className="w-full gradient-neural text-white" 
                      disabled
                      data-testid="pro-plan-btn"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Plan Actual
                    </Button>
                  ) : isOnTrial ? (
                    <Button 
                      className="w-full gradient-neural glow-neural text-white" 
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={processingPayment}
                      data-testid="subscribe-btn"
                    >
                      {processingPayment ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Suscribirse por €{plan.promo_price?.toFixed(2)}/mes
                        </>
                      )}
                    </Button>
                  ) : canStartTrial ? (
                    <Button 
                      className="w-full gradient-neural glow-neural text-white" 
                      onClick={handleStartTrial}
                      disabled={startingTrial}
                      data-testid="start-trial-card-btn"
                    >
                      {startingTrial ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Activando...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Empezar 7 Días Gratis
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button 
                      className="w-full gradient-neural glow-neural text-white" 
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={processingPayment}
                      data-testid="subscribe-btn"
                    >
                      {processingPayment ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Suscribirse por €{plan.promo_price?.toFixed(2)}/mes
                        </>
                      )}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* FAQ Section */}
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold mb-6">Preguntas Frecuentes</h2>
            <div className="grid gap-6 md:grid-cols-2 text-left max-w-3xl mx-auto">
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold mb-2">¿Cómo funciona la prueba gratuita?</h3>
                  <p className="text-muted-foreground text-sm">
                    Tienes 7 días para probar todas las funciones Pro sin ningún compromiso. No necesitas tarjeta de crédito.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold mb-2">¿Puedo cancelar en cualquier momento?</h3>
                  <p className="text-muted-foreground text-sm">
                    Sí, puedes cancelar tu suscripción cuando quieras. No hay compromisos a largo plazo.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold mb-2">¿Qué pasa después de la prueba?</h3>
                  <p className="text-muted-foreground text-sm">
                    Después de los 7 días puedes suscribirte por €4.99/mes (2 meses) y luego €14.99/mes, o volver al plan gratuito.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold mb-2">¿Qué métodos de pago aceptan?</h3>
                  <p className="text-muted-foreground text-sm">
                    Aceptamos todas las tarjetas de crédito y débito principales a través de Stripe.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
